import { MAIN_PERMISSION, SUB_PERMISSION } from "constant/constants";
import { lazy } from "react";
import {
  FaCalendar,
  FaFile,
  FaGasPump,
  FaHouse,
  FaMoneyBill,
  FaPercent,
  FaUser,
  FaUsers,
  FaUserTie,
} from "react-icons/fa6";
import { HiDocumentText } from "react-icons/hi";
import { MdOutlineContactSupport } from "react-icons/md";
import { RiFolderHistoryFill, RiListSettingsLine } from "react-icons/ri";

const Dashboard = lazy(() => import("../pages/Dashboard"));
const Repo = lazy(() => import("../pages/Repo"));
const CrudManagement = lazy(() => import("../pages/CrudManagement"));
const Login = lazy(() => import("../pages/Login"));
const AccountSettings = lazy(() => import("../pages/AccountSettings"));
const Notifications = lazy(() => import("../pages/Notifications"));
const UserManagement = lazy(() => import("../pages/UserManagement"));
const UserDetails = lazy(() => import("../pages/UserManagement/UserDetails"));
const DriverOrderHistory = lazy(() =>
  import("../pages/UserManagement/DriverOrderHistory")
);
const BookingManagement = lazy(() => import("../pages/BookingManagement"));
const RoleManagement = lazy(() => import("../pages/RoleManagement"));
const AddRole = lazy(() => import("../pages/AddRole"));
const EditRole = lazy(() => import("../pages/EditRole"));
const BookingManagementDetail = lazy(() =>
  import("../pages/BookingManagementDetail")
);
const SubscriptionManagement = lazy(() =>
  import("../pages/SubscriptionManagement")
);
const EditSubscription = lazy(() => import("../pages/EditSubscription"));
const FuelManagement = lazy(() => import("../pages/FuelManagement"));
const AddFuel = lazy(() => import("../pages/AddFuel"));
const EditFuel = lazy(() => import("../pages/EditFuel"));
const DiscountManagement = lazy(() => import("../pages/DiscountManagement"));
const PaymentAnalytics = lazy(() => import("../pages/PaymentAnalytics"));
const AddSubAdmin = lazy(() => import("../pages/AddSubAdmin"));
const EditSubAdmin = lazy(() => import("../pages/EditSubAdmin"));
const AddDriver = lazy(() => import("../pages/AddDriver"));
const EditDriver = lazy(() => import("../pages/EditDriver"));
const StaffManagement = lazy(() => import("../pages/StaffManagement"));
const SupportManagement = lazy(() => import("../pages/SupportManagement"));
const FuelHistory = lazy(() => import("../pages/FuelHistory"));
const Cms = lazy(() => import("../pages/Cms"));

export const routes = [
  {
    path: "/login",
    exact: true,
    element: <Login />,
    protected: false,
    showInSidebar: false,
  },
  {
    path: "/",
    exact: true,
    protected: true,
    showInSidebar: true,
    title: "Dashboard",
    icon: <FaHouse />,
    element: <Dashboard />,
  },
  {
    path: "/user-management",
    exact: true,
    protected: true,
    showInSidebar: true,
    title: "User Management",
    icon: <FaUsers />,
    element: <UserManagement />,
  },
  {
    path: "/staff-management",
    exact: true,
    protected: true,
    showInSidebar: true,
    title: "Staff Management",
    icon: <FaUserTie />,
    element: <StaffManagement />,
  },
  {
    path: "/staff-management/add",
    exact: true,
    protected: true,
    showInSidebar: false,
    title: "Add Sub Admin",
    requiredPermission: MAIN_PERMISSION.STAFF,
    requiredSubPermission: SUB_PERMISSION.CREATE,
    element: <AddSubAdmin />,
  },
  {
    path: "/staff-management/edit/:id",
    exact: true,
    protected: true,
    showInSidebar: false,
    title: "Edit Sub Admin",
    requiredPermission: MAIN_PERMISSION.STAFF,
    requiredSubPermission: SUB_PERMISSION.UPDATE,
    element: <EditSubAdmin />,
  },

  {
    path: "/user-management/add",
    exact: true,
    protected: true,
    showInSidebar: false,
    title: "Add Driver",
    requiredPermission: MAIN_PERMISSION.USER,
    requiredSubPermission: SUB_PERMISSION.CREATE,
    element: <AddDriver />,
  },
  {
    path: "/user-management/edit/:id",
    exact: true,
    protected: true,
    showInSidebar: false,
    title: "Edit Driver",
    requiredPermission: MAIN_PERMISSION.USER,
    requiredSubPermission: SUB_PERMISSION.UPDATE,
    element: <EditDriver />,
  },
  {
    path: "/user-management/order-history/:id",
    exact: true,
    protected: true,
    showInSidebar: false,
    title: "Driver Order History",
    element: <DriverOrderHistory />,
  },
  {
    path: "/user-management/:id",
    exact: true,
    protected: true,
    showInSidebar: false,
    title: "User Details",
    element: <UserDetails />,
  },
  {
    path: "/booking-management",
    exact: true,
    protected: true,
    showInSidebar: true,
    title: "Booking Management",
    icon: <FaCalendar />,
    element: <BookingManagement />,
  },
  {
    path: "/booking-management/:id",
    exact: true,
    protected: true,
    showInSidebar: false,
    title: "Booking Details",
    requiredPermission: MAIN_PERMISSION.BOOKING,
    requiredSubPermission: SUB_PERMISSION.READ,
    element: <BookingManagementDetail />,
  },

  {
    path: "/subscription-management",
    exact: true,
    protected: true,
    showInSidebar: true,
    title: "Subscription Management",
    icon: <HiDocumentText />,
    element: <SubscriptionManagement />,
  },
  {
    path: "/subscription-management/edit",
    exact: true,
    protected: true,
    showInSidebar: false,
    title: "Edit Subscription",
    requiredPermission: MAIN_PERMISSION.SUBSCRIPTION,
    requiredSubPermission: SUB_PERMISSION.UPDATE,
    element: <EditSubscription />,
  },
  {
    path: "/fuel-management",
    exact: true,
    protected: true,
    showInSidebar: true,
    title: "Fuel Management",
    icon: <FaGasPump />,
    element: <FuelManagement />,
  },
  {
    path: "/fuel-history",
    exact: true,
    protected: true,
    showInSidebar: true,
    title: "Fuel History",
    icon: <RiFolderHistoryFill />,
    element: <FuelHistory />,
  },
  {
    path: "/discount-management",
    exact: true,
    protected: true,
    showInSidebar: true,
    title: "Discount Management",
    icon: <FaPercent />,
    element: <DiscountManagement />,
  },
  {
    path: "/fuel-management/add",
    exact: true,
    protected: true,
    showInSidebar: false,
    title: "Add Fuel",
    requiredPermission: MAIN_PERMISSION.FUEL,
    requiredSubPermission: SUB_PERMISSION.CREATE,
    element: <AddFuel />,
  },
  {
    path: "/fuel-management/edit/:id",
    exact: true,
    protected: true,
    showInSidebar: false,
    title: "Edit Fuel",
    requiredPermission: MAIN_PERMISSION.FUEL,
    requiredSubPermission: SUB_PERMISSION.UPDATE,
    element: <EditFuel />,
  },
  {
    path: "/support-management",
    exact: true,
    protected: true,
    showInSidebar: true,
    title: "Support Management",
    icon: <MdOutlineContactSupport />,
    element: <SupportManagement />,
  },
  {
    path: "/payment-analytics",
    exact: true,
    protected: true,
    showInSidebar: true,
    title: "Payment Analytics",
    icon: <FaMoneyBill />,
    element: <PaymentAnalytics />,
  },
  {
    path: "/role-management",
    exact: true,
    protected: true,
    showInSidebar: true,
    title: "Role Management",
    icon: <FaUsers />,
    element: <RoleManagement />,
  },
  {
    path: "/role-management/add",
    exact: true,
    protected: true,
    showInSidebar: false,
    title: "Add Role",
    requiredPermission: MAIN_PERMISSION.ROLE,
    requiredSubPermission: SUB_PERMISSION.CREATE,
    element: <AddRole />,
  },
  {
    path: "/role-management/edit/:id",
    exact: true,
    protected: true,
    showInSidebar: false,
    title: "Edit Role",
    requiredPermission: MAIN_PERMISSION.ROLE,
    requiredSubPermission: SUB_PERMISSION.UPDATE,
    element: <EditRole />,
  },

  {
    path: "/crud-management",
    exact: true,
    protected: true,
    showInSidebar: true,
    title: "CRUD Management",
    icon: <RiListSettingsLine />,
    element: <CrudManagement />,
  },
  {
    path: "/cms",
    exact: true,
    protected: true,
    showInSidebar: true,
    title: "CMS",
    icon: <FaFile />,
    element: <Cms />,
  },
  {
    path: "/account-settings",
    exact: true,
    protected: true,
    showInSidebar: true,
    title: "Profile Settings",
    icon: <FaUser />,
    element: <AccountSettings />,
  },

  {
    path: "/notifications",
    exact: true,
    protected: true,
    showInSidebar: false,
    title: "Notifications",
    element: <Notifications />,
  },
];
